import { useCallback, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { MonitorX, PhoneOff, Trash2, UserRoundX } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { useToast } from '@/providers/UiProviders'
import { fetchReregBarriers, removeReregBarrier, BAN_STR as S, type ReregRuleRow } from './strikesApi'

/**
 * A8.18 — Qayta ro'yxatdan o'tishni oldini olish. JONLI: «bloklangan
 * identifikatorlar» = IP/qurilma bloklari (A8.16 `/admin/ip-blocks`) + telefon
 * bloklari (A8.17 `/admin/phone-blocks`) birlashtirilgan REAL ko'rinishi. Har
 * qatorni o'z manba-jadvalidan o'chirish mumkin (to'siqni olib tashlaydi).
 * Yangi identifikator qo'shish alohida A8.16/A8.17 ekranlaridan qilinadi.
 */
export function ReregistrationPreventionScreen() {
  const { toast } = useToast()
  const [reloadKey, setReloadKey] = useState(0)

  const columns = useMemo<AdminColumn<ReregRuleRow>[]>(
    () => [
      {
        key: 'signal',
        header: S.signal,
        type: 'enum',
        width: 130,
        options: [
          { value: 'ip', label: S.sigIp, status: 'review' },
          { value: 'device', label: S.sigDevice, status: 'pending' },
          { value: 'subnet', label: S.sigSubnet, status: 'banned' },
          { value: 'phone', label: S.sigPhone, status: 'review' },
          { value: 'prefix', label: S.sigPrefix, status: 'pending' },
        ],
      },
      {
        key: 'match',
        header: S.match,
        type: 'text',
        minWidth: 180,
        render: (r) => <span className="font-mono tabular text-primary">{r.match}</span>,
      },
      { key: 'reason', header: S.reason, type: 'longtext', minWidth: 200 },
      { key: 'hits', header: S.hits, type: 'number', width: 100, align: 'right' },
      { key: 'created_at', header: S.created, type: 'date', width: 150 },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<ReregRuleRow>[]>(
    () => [
      {
        id: 'remove',
        label: S.remove,
        icon: <Trash2 size={14} />,
        danger: true,
        permission: PERMISSIONS.USERS_BAN,
        onSelect: async (r) => {
          try {
            await removeReregBarrier(r)
            toast(S.removed, { tone: 'success' })
            setReloadKey((k) => k + 1)
          } catch {
            toast(S.failed, { tone: 'error' })
          }
        },
      },
    ],
    [toast],
  )

  const fetcher = useCallback(fetchReregBarriers, [])
  return (
    <>
      <PageHeader
        title={S.rereg}
        screenId="A8.18"
        description={S.reregNote}
        actions={
          <div className="flex items-center gap-2">
            <Link
              to="/blocks/devices"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <MonitorX size={13} />
              {S.deviceBlocks}
            </Link>
            <Link
              to="/blocks/phones"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
            >
              <PhoneOff size={13} />
              {S.phoneBlocks}
            </Link>
          </div>
        }
      />
      <DataTable<ReregRuleRow>
        key={reloadKey}
        screenId="A8.18"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => r.id}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <UserRoundX size={28} strokeWidth={1.5} />, title: S.rereg }}
      />
    </>
  )
}
